import { useEffect, useRef, useState } from 'react';

import ButtonLink from '../../../components/Button/ButtonLink';
import Dialog from '../../../components/Dialog/';
import { Heading2, Paragraph } from '../../../components/Text';

import style from './external-link-modal.module.css';

interface ExternalLinkModalProps {
  buttonText?: null | string;
  clickListenerScopeId?: string;
  description?: null | string;
  title?: null | string;
}

const ExternalLinkModal = ({
  buttonText,
  clickListenerScopeId,
  description,
  title,
}: ExternalLinkModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [interceptedUrl, setInterceptedUrl] = useState<null | string>(null);

  useEffect(() => {
    if (!clickListenerScopeId) {
      return;
    }

    const listener = (e: MouseEvent) => {
      if (e.target instanceof HTMLAnchorElement) {
        const href = e.target.href;

        if (href) {
          try {
            const url = new URL(href);
            const currentDomain = window.location.hostname;

            // Only intercept http and https protocols
            if (url.protocol === 'http:' || url.protocol === 'https:') {
              // Check if the clicked element has the modal external link class
              // - if so, allow normal navigation
              if (e.target.classList.contains('modal-external-link')) {
                return;
              }

              // Check if it's an external link (different domain)
              if (url.hostname !== currentDomain) {
                e.preventDefault();
                setInterceptedUrl(href);
                dialogRef.current?.showModal();
              }
            }
          }
          catch {
            // If URL parsing fails, allow normal navigation
            console.warn('Failed to parse URL:', href);
          }
        }
      }
    };

    const wrapperElement = document.getElementById(clickListenerScopeId);
    wrapperElement?.addEventListener('click', listener);

    return () => {
      wrapperElement?.removeEventListener('click', listener);
    };
  }, [clickListenerScopeId]);

  const link = {
    target: '_blank',
    title: buttonText ?? 'Proceed',
    url: interceptedUrl ?? '',
  };

  return (
    <Dialog
      ref={dialogRef}
    >
      <Heading2>{title ?? 'External Link'}</Heading2>
      <Paragraph className={style.description}>
        {description ?? 'You are about to visit an external website.'}
      </Paragraph>
      <ButtonLink
        buttonColor="black"
        className="modal-external-link"
        linkFragment={link}
      />
    </Dialog>
  );
};

export default ExternalLinkModal;
